import { supabase } from '../lib/supabase';

/**
 * Recommendation Analytics Service
 * Handles all Supabase operations for AI recommendation performance analytics
 */

/**
 * Fetch aggregated analytics for all recommendation types
 * @returns {Promise<Array>} Array of analytics records
 */
export const getRecommendationAnalytics = async () => {
  try {
    const { data, error } = await supabase?.from('ai_recommendation_analytics')?.select('*')?.order('last_updated', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(record => ({
      id: record?.id,
      contentId: record?.content_id,
      recommendationType: record?.recommendation_type,
      totalImpressions: record?.total_impressions || 0,
      totalClicks: record?.total_clicks || 0,
      totalCompletions: record?.total_completions || 0,
      totalFavorites: record?.total_favorites || 0,
      totalDismissals: record?.total_dismissals || 0,
      clickThroughRate: record?.click_through_rate || 0,
      completionRate: record?.completion_rate || 0,
      favoriteRate: record?.favorite_rate || 0,
      dismissalRate: record?.dismissal_rate || 0,
      avgConfidence: record?.avg_confidence || 0,
      lastUpdated: record?.last_updated
    })) || [];
  } catch (error) {
    console.error('Error fetching recommendation analytics:', error);
    throw error;
  }
};

/**
 * Get top performing content by recommendation type
 * @param {string} recommendationType - Type of recommendation ('song', 'role', 'musical')
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Array of top performers
 */
export const getTopPerformers = async (recommendationType, limit = 10) => {
  try {
    const { data, error } = await supabase?.from('ai_recommendation_analytics')?.select('*')?.eq('recommendation_type', recommendationType)?.order('click_through_rate', { ascending: false })?.limit(limit);

    if (error) throw error;

    // Fetch content details based on type
    const analyticsData = data || [];
    const contentIds = analyticsData?.map(a => a?.content_id);
    
    let contentData = [];
    
    if (contentIds?.length > 0) {
      if (recommendationType === 'song') {
        const { data: songs } = await supabase?.from('songs')?.select('id, title, musical_id, musicals(title)')?.in('id', contentIds);
        contentData = songs || [];
      } else if (recommendationType === 'role') {
        const { data: roles } = await supabase?.from('roles')?.select('id, character_name, musical_id, musicals(title)')?.in('id', contentIds);
        contentData = roles || [];
      } else if (recommendationType === 'musical') {
        const { data: musicals } = await supabase?.from('musicals')?.select('id, title, composer')?.in('id', contentIds);
        contentData = musicals || [];
      }
    }

    // Merge analytics with content data
    return analyticsData?.map(analytics => {
      const content = contentData?.find(c => c?.id === analytics?.content_id);
      return {
        id: analytics?.id,
        contentId: analytics?.content_id,
        recommendationType: analytics?.recommendation_type,
        title: content?.title || content?.character_name || 'Unknown',
        musicalTitle: content?.musicals?.title || content?.composer || '',
        totalImpressions: analytics?.total_impressions || 0,
        totalClicks: analytics?.total_clicks || 0,
        clickThroughRate: analytics?.click_through_rate || 0,
        completionRate: analytics?.completion_rate || 0,
        favoriteRate: analytics?.favorite_rate || 0,
        avgConfidence: analytics?.avg_confidence || 0
      };
    });
  } catch (error) {
    console.error('Error fetching top performers:', error);
    throw error;
  }
};

/**
 * Get time-series data for recommendation interactions
 * @param {number} days - Number of days to fetch data for
 * @returns {Promise<Array>} Array of daily interaction counts
 */
export const getInteractionTimeSeries = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase?.from('ai_recommendation_interactions')?.select('created_at, interaction_type, impression_id')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });

    if (error) throw error;

    // Group by date and interaction type
    const timeSeriesData = {};
    
    data?.forEach(interaction => {
      const date = new Date(interaction.created_at)?.toLocaleDateString();
      if (!timeSeriesData?.[date]) {
        timeSeriesData[date] = {
          date,
          clicks: 0,
          completions: 0,
          favorites: 0,
          dismissals: 0
        };
      }
      
      const type = interaction?.interaction_type;
      if (type === 'clicked') timeSeriesData[date].clicks++;
      else if (type === 'completed') timeSeriesData[date].completions++;
      else if (type === 'favorited') timeSeriesData[date].favorites++;
      else if (type === 'dismissed') timeSeriesData[date].dismissals++;
    });

    return Object.values(timeSeriesData);
  } catch (error) {
    console.error('Error fetching interaction time series:', error);
    throw error;
  }
};

/**
 * Get recommendation impressions with context
 * @param {number} limit - Number of results to return
 * @returns {Promise<Array>} Array of recent impressions
 */
export const getRecentImpressions = async (limit = 50) => {
  try {
    const { data, error } = await supabase?.from('ai_recommendation_impressions')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(impression => ({
      id: impression?.id,
      userId: impression?.user_id,
      contentId: impression?.content_id,
      recommendationType: impression?.recommendation_type,
      confidenceScore: impression?.confidence_score,
      reasoning: impression?.reasoning,
      context: impression?.context,
      createdAt: impression?.created_at
    })) || [];
  } catch (error) {
    console.error('Error fetching recent impressions:', error);
    throw error;
  }
};

/**
 * Get summary statistics for all recommendations
 * @returns {Promise<Object>} Summary statistics object
 */
export const getSummaryStatistics = async () => {
  try {
    const { data, error } = await supabase?.from('ai_recommendation_analytics')?.select('*');

    if (error) throw error;

    const stats = {
      totalImpressions: 0,
      totalClicks: 0,
      totalCompletions: 0,
      totalFavorites: 0,
      totalDismissals: 0,
      avgClickThroughRate: 0,
      avgCompletionRate: 0,
      avgFavoriteRate: 0,
      avgConfidence: 0
    };

    if (data && data?.length > 0) {
      data?.forEach(record => {
        stats.totalImpressions += record?.total_impressions || 0;
        stats.totalClicks += record?.total_clicks || 0;
        stats.totalCompletions += record?.total_completions || 0;
        stats.totalFavorites += record?.total_favorites || 0;
        stats.totalDismissals += record?.total_dismissals || 0;
      });

      const count = data?.length;
      stats.avgClickThroughRate = data?.reduce((sum, r) => sum + (r?.click_through_rate || 0), 0) / count;
      stats.avgCompletionRate = data?.reduce((sum, r) => sum + (r?.completion_rate || 0), 0) / count;
      stats.avgFavoriteRate = data?.reduce((sum, r) => sum + (r?.favorite_rate || 0), 0) / count;
      stats.avgConfidence = data?.reduce((sum, r) => sum + (r?.avg_confidence || 0), 0) / count;
    }

    return stats;
  } catch (error) {
    console.error('Error fetching summary statistics:', error);
    throw error;
  }
};

export default {
  getRecommendationAnalytics,
  getTopPerformers,
  getInteractionTimeSeries,
  getRecentImpressions,
  getSummaryStatistics
};